import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { fromJS } from 'immutable';

import {
    fetchExtension,
    fetchMobileSubscription,
    updateMobileSubscription,
} from 'flow/actions/extension-actions';
import Page from 'flow/components/Setting/Page';
import SettingHeader from 'flow/components/Setting/SettingHeader';
import SettingRow from 'flow/components/Setting/SettingRow';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useMobile from 'flow/hooks/useMobile';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { getAllSimcardsForExtension } from 'flow/selectors/extension-selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Simcard } from 'flow/types/Extension';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import {
    getRoamingCurrencySymbol,
    getRoamingCurrentUsageInCurrencyString,
    getRoamingCutOffPriceString,
    getRoamingUsagePercentageRounded,
} from 'flow/utils/mobile-data-utils';
import { generatePath } from 'flow/utils/router-utils';
import { getSimcardInformation } from 'flow/utils/sim-card-utils';

export type mobileParams = {
    simKey: string;
    extensionKey: ExtensionKey;
};

type RoamingDataType = {
    roamingUsagePercentage: number;
};

type SimcardType = {
    key: string;
    roamingData: RoamingDataType;
};

type UpdateSubscription = {
    simcards: SimcardType[];
};

const SettingsMobileRoamingData = () => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const { simKey, extensionKey } = useParams<mobileParams>();
    const history = useHistory();

    const allSimcards = useAppSelector((state) =>
        getAllSimcardsForExtension(state, extensionKey),
    );

    const simcard = allSimcards.find(
        (sim: TypedMap<Simcard>) => sim.get('key') === simKey,
    );
    const { mobileSubscription } = useMobile(extensionKey);

    const handleUpdateMobileSubscription = (
        mobileSubscriptionUpdate: UpdateSubscription,
    ) => {
        dispatch(
            updateMobileSubscription(extensionKey, mobileSubscriptionUpdate),
        );
    };

    // Fetch mobile subscription data if not already loaded
    useEffect(() => {
        if (extensionKey && (!simcard || simcard.isEmpty())) {
            dispatch(fetchMobileSubscription(extensionKey));
            dispatch(
                fetchExtension(extensionKey, 'all', 'live', 'mno_subscription'),
            );
        }
    }, [dispatch, extensionKey, simcard]);

    if (!mobileSubscription && !simcard) {
        return null;
    }

    const handleCutoffReset = (simcardKey: string) => {
        const updateSubscription = fromJS({
            simcards: [
                {
                    key: simcardKey,
                    roamingData: {
                        roamingUsagePercentage: 0,
                    },
                },
            ],
        });

        handleUpdateMobileSubscription(updateSubscription);
    };

    const handleCutoffToggle = () => {
        const updatedMobileSubscription = (() => {
            const state = mobileSubscription.get(
                // @ts-expect-error
                'costControlServiceState',
                'unknown',
            );

            if (state === 'active') {
                return { costControlServiceState: 'inactive' };
            } else if (state === 'inactive') {
                return { costControlServiceState: 'active' };
            } else {
                throw new Error(
                    'Failed to toggle cost control when previous state is: ' +
                        state,
                );
            }
        })();

        handleUpdateMobileSubscription(fromJS(updatedMobileSubscription));
    };

    const simcardInfo = getSimcardInformation(simcard, mobileSubscription);
    const usagePercentage = getRoamingUsagePercentageRounded(simcardInfo);
    const cutOffValueText = getRoamingCutOffPriceString(simcardInfo);
    const currency = getRoamingCurrencySymbol(simcardInfo);
    const rcState = simcardInfo
        .get('roamingData')
        ?.get('costControlServiceState', 'unknown');

    return (
        <Page
            header="settings.usage.roaming_data"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.mobile.simKey'], {
                        extensionKey,
                        simKey,
                    }),
                )
            }
        >
            {rcState === 'active' && (
                <>
                    <div className="mb-3 text-sm text-gray-600">
                        {i18next.t('settings.usage.roaming_data_info', {
                            currency,
                        })}
                    </div>
                    <Divider />
                </>
            )}

            <SettingRow
                cta={
                    <Toggle
                        value={rcState === 'active'}
                        onChange={handleCutoffToggle}
                        disabled={rcState === 'unknown'}
                        text={i18next.t('settings.usage.cutoff_active', {
                            cutoffValueAndCurrency: cutOffValueText,
                        })}
                    />
                }
            >
                {i18next.t('settings.usage.cutoff_active', {
                    cutoffValueAndCurrency: cutOffValueText,
                })}
            </SettingRow>

            <Divider />

            <SettingHeader>
                {i18next.t('settings.usage.roaming_total')}
            </SettingHeader>

            <progress
                className="progress-unfilled:rounded-sm progress-unfilled:bg-gray-200 progress-filled:rounded-s-sm progress-filled:bg-main mb-3 h-6 w-full"
                value={usagePercentage}
                max="100"
            >
                {usagePercentage}
            </progress>
            {rcState === 'active' && (
                <div className="flex flex-row items-center text-sm">
                    <div className="font-bold text-gray-700">
                        {getRoamingCurrentUsageInCurrencyString(simcardInfo)}
                    </div>
                    <div className="text-gray-600">
                        &nbsp;{`(${usagePercentage}%)`}&nbsp;
                        {i18next.t('settings.usage.of_cutoff_limit')}&nbsp;
                    </div>
                    <div className="font-bold text-gray-700">
                        {cutOffValueText}
                    </div>
                </div>
            )}

            <Button
                color="ghost"
                onClick={() => handleCutoffReset(simcardInfo.get('key'))}
            >
                {i18next.t('settings.usage.reset_button')}
            </Button>
            {rcState === 'active' && (
                <div className="mt-3 text-sm text-gray-600">
                    {i18next.t('settings.usage.reset_explain', {
                        cutoffValueAndCurrency: cutOffValueText,
                    })}
                </div>
            )}
        </Page>
    );
};

export default SettingsMobileRoamingData;
